import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { ConfigMapListItem, DocumentTypeDto } from "../interfaces/configMap";

interface ConfigMapListDraftState {
  draft: ConfigMapListItem[] | null;
  sourceDraft: ConfigMapListItem[] | null;
  isDirty: boolean;
  isSaving: boolean;
}

const initialState: ConfigMapListDraftState = {
  draft: null,
  sourceDraft: null,
  isDirty: false,
  isSaving: false,
};

const configMapListDraftSlice = createSlice({
  name: "configMapListDraft",
  initialState,
  reducers: {
    initListDraft: (state, action: PayloadAction<ConfigMapListItem[]>) => {
      state.draft = action.payload;
      state.sourceDraft = action.payload;
      state.isDirty = false;
      state.isSaving = false;
    },

    resetListDraft: (state) => {
      state.draft = state.sourceDraft ? [...state.sourceDraft] : null;
      state.isDirty = false;
    },

    markListClean: (state) => {
      state.sourceDraft = state.draft ? [...state.draft] : null;
      state.isDirty = false;
      state.isSaving = false;
    },

    setListSaving: (state, action: PayloadAction<boolean>) => {
      state.isSaving = action.payload;
    },

    addConfigMap: (state, action: PayloadAction<{ display_name: string }>) => {
      if (!state.draft) {
        return;
      }
      state.draft.push({
        id: `temp_${crypto.randomUUID()}`,
        display_name: action.payload.display_name,
        groupBlockCount: 0,
        entityCount: 0,
        categorizationCount: 0,
        modifiedDate: new Date().toISOString(),
        modifiedBy: "",
        _isNew: true,
      } as ConfigMapListItem & { _isNew: boolean });
      state.isDirty = true;
    },

    renameConfigMap: (
      state,
      action: PayloadAction<{ id: string; display_name: string }>,
    ) => {
      if (!state.draft) {
        return;
      }
      const item = state.draft.find((cm) => cm.id === action.payload.id);
      if (item) {
        item.display_name = action.payload.display_name;
        state.isDirty = true;
      }
    },

    deleteConfigMap: (state, action: PayloadAction<string>) => {
      if (!state.draft) {
        return;
      }
      state.draft = state.draft.filter((cm) => cm.id !== action.payload);
      state.isDirty = true;
    },

    addDocumentType: (
      state,
      action: PayloadAction<{
        configMapId: string;
        documentType: DocumentTypeDto;
      }>,
    ) => {
      if (!state.draft) {
        return;
      }
      const cm = state.draft.find((cm) => cm.id === action.payload.configMapId);
      if (!cm) {
        return;
      }
      if (!cm.documentTypes) {
        cm.documentTypes = [];
      }
      cm.documentTypes.push(action.payload.documentType);
      state.isDirty = true;
    },

    removeDocumentType: (
      state,
      action: PayloadAction<{ configMapId: string; documentTypeId: string }>,
    ) => {
      if (!state.draft) {
        return;
      }
      const cm = state.draft.find((cm) => cm.id === action.payload.configMapId);
      if (!cm || !cm.documentTypes) {
        return;
      }
      cm.documentTypes = cm.documentTypes.filter(
        (dt) => dt.id !== action.payload.documentTypeId,
      );
      state.isDirty = true;
    },

    addImportedConfigMap: (state, action: PayloadAction<ConfigMapListItem>) => {
      if (!state.draft) {
        state.draft = [];
      }
      if (!state.sourceDraft) {
        state.sourceDraft = [];
      }
      // Replace if already in list (overwrite mode), otherwise append.
      const draftIdx = state.draft.findIndex(
        (cm) => cm.id === action.payload.id,
      );
      if (draftIdx >= 0) {
        state.draft[draftIdx] = action.payload;
      } else {
        state.draft.push(action.payload);
      }
      const sourceIdx = state.sourceDraft.findIndex(
        (cm) => cm.id === action.payload.id,
      );
      if (sourceIdx >= 0) {
        state.sourceDraft[sourceIdx] = action.payload;
      } else {
        state.sourceDraft.push(action.payload);
      }
    },

    cloneConfigMap: (
      state,
      action: PayloadAction<{ sourceId: string; display_name: string }>,
    ) => {
      if (!state.draft) {
        return;
      }
      const source = state.draft.find(
        (cm) => cm.id === action.payload.sourceId,
      );
      if (!source) {
        return;
      }
      state.draft.push({
        id: `temp_${crypto.randomUUID()}`,
        display_name: action.payload.display_name,
        groupBlockCount: source.groupBlockCount,
        entityCount: source.entityCount,
        categorizationCount: source.categorizationCount,
        modifiedDate: new Date().toISOString(),
        modifiedBy: "",
        _isNew: true,
        _cloneSourceId: action.payload.sourceId,
      } as ConfigMapListItem & { _isNew: boolean; _cloneSourceId: string });
      state.isDirty = true;
    },
  },
});

export const {
  initListDraft,
  resetListDraft,
  markListClean,
  setListSaving,
  addConfigMap,
  renameConfigMap,
  deleteConfigMap,
  cloneConfigMap,
  addDocumentType,
  removeDocumentType,
  addImportedConfigMap,
} = configMapListDraftSlice.actions;

export default configMapListDraftSlice.reducer;

export const selectListDraft = (state: RootState) =>
  state.configMapListDraftReducer.draft;
export const selectListSourceDraft = (state: RootState) =>
  state.configMapListDraftReducer.sourceDraft;
export const selectListIsDirty = (state: RootState) =>
  state.configMapListDraftReducer.isDirty;
export const selectListIsSaving = (state: RootState) =>
  state.configMapListDraftReducer.isSaving;
